/**
 * SupportHubPanel — Expandable Support Hub Panel
 *
 * A floating panel that anchors to the DraggableFab position.
 * Contains a tab bar, universal search, and renders the active tab content.
 * Supports click-outside-to-close and Escape key.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
    Box,
    IconButton,
    TextField,
    InputAdornment,
    Typography,
    Tooltip,
    Grow,
    useTheme,
    useMediaQuery,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Home as HomeIcon,
    AutoAwesome as AIIcon,
    MenuBook as HelpIcon,
    BugReport as BugIcon,
    Forum as ContactIcon,
} from '@mui/icons-material';
import { useSupportHub, type SupportHubTab } from './SupportHubContext';
import HomeTab from './tabs/HomeTab';
import AIAssistantTab from './tabs/AIAssistantTab';
import HelpCenterTab from './tabs/HelpCenterTab';
import ReportIssueTab from './tabs/ReportIssueTab';
import ContactTab from './tabs/ContactTab';
import { PANEL_WIDTH, getPanelHeight } from './panelGeometry';


interface TabConfig {
    id: SupportHubTab;
    label: string;
    icon: React.ReactNode;
    component: React.ReactNode;
}

const TABS: TabConfig[] = [
    { id: 'home', label: 'Home', icon: <HomeIcon fontSize="small" />, component: <HomeTab /> },
    { id: 'ai', label: 'AI Assistant', icon: <AIIcon fontSize="small" />, component: <AIAssistantTab /> },
    { id: 'help', label: 'Help Center', icon: <HelpIcon fontSize="small" />, component: <HelpCenterTab /> },
    { id: 'report', label: 'Report Issue', icon: <BugIcon fontSize="small" />, component: <ReportIssueTab /> },
    { id: 'contact', label: 'Contact Us', icon: <ContactIcon fontSize="small" />, component: <ContactTab /> },
];

export const SupportHubPanel: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {
        isPanelOpen,
        closePanel,
        activeTab,
        setActiveTab,
        searchQuery,
        setSearchQuery,
        fabPosition,
    } = useSupportHub();

    const panelRef = useRef<HTMLDivElement>(null);

    // Click outside to close
    useEffect(() => {
        if (!isPanelOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (
                panelRef.current &&
                !panelRef.current.contains(target) &&
                !target.closest('[data-testid="support-hub-fab-wrapper"]') &&
                !target.closest('.MuiPopover-root') &&
                !target.closest('.MuiMenu-root')
            ) {
                closePanel();
            }
        };
        // Delay to prevent the opening click from immediately closing
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isPanelOpen, closePanel]);

    // Escape key to close
    useEffect(() => {
        if (!isPanelOpen) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closePanel();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isPanelOpen, closePanel]);

    const handleTabClick = useCallback(
        (tab: SupportHubTab) => {
            setActiveTab(tab);
            setSearchQuery('');
        },
        [setActiveTab, setSearchQuery],
    );

    // Recompute panel position on window resize while open. `resize` fires on every
    // pixel of a drag, and each tick re-renders the panel and whichever tab is
    // mounted, so coalesce them onto animation frames.
    const [, setResizeTick] = useState(0);
    useEffect(() => {
        if (!isPanelOpen) return;
        let rafId: number | null = null;
        const handleResize = () => {
            if (rafId !== null) return;
            rafId = window.requestAnimationFrame(() => {
                rafId = null;
                setResizeTick((tick) => tick + 1);
            });
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (rafId !== null) window.cancelAnimationFrame(rafId);
        };
    }, [isPanelOpen]);

    // Compute panel position based on FAB
    const getPanelStyle = (): React.CSSProperties => {
        if (isMobile) {
            return {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                top: 0,
                zIndex: 1500,
            };
        }

        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const panelHeight = getPanelHeight(viewportHeight);

        const isLeft = fabPosition.side === 'left';
        const panelX = isLeft ? fabPosition.x : fabPosition.x + 56 - PANEL_WIDTH;
        const panelY = Math.max(
            16,
            Math.min(fabPosition.y - panelHeight + 56, viewportHeight - panelHeight - 16),
        );

        return {
            position: 'fixed',
            left: Math.max(8, Math.min(panelX, viewportWidth - PANEL_WIDTH - 8)),
            top: panelY,
            width: PANEL_WIDTH,
            height: panelHeight,
            zIndex: 1500,
        };
    };

    const isDark = theme.palette.mode === 'dark';
    const activeTabConfig = TABS.find((t) => t.id === activeTab) || TABS[0];

    if (!isPanelOpen) return null;

    return (
        <Grow in={isPanelOpen} timeout={250}>
            <Box
                ref={panelRef}
                sx={{
                    ...getPanelStyle(),
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: isDark
                        ? alpha(theme.palette.background.raised ?? theme.palette.background.paper, 0.97)
                        : alpha('#FFFFFF', 0.97),
                    backdropFilter: 'blur(20px)',
                    borderRadius: isMobile ? 0 : '16px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    boxShadow: isDark
                        ? '0 8px 40px rgba(0,0,0,0.6)'
                        : '0 8px 40px rgba(21, 33, 27,0.18)',
                    overflow: 'hidden',
                }}
                data-testid="support-hub-panel"
            >
                {/* ── Header ── */}
                <Box
                    sx={{
                        p: 1.5,
                        pb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                        background: isDark
                            ? 'linear-gradient(135deg, rgba(91,141,184,0.1) 0%, transparent 100%)'
                            : 'linear-gradient(135deg, rgba(21, 33, 27,0.03) 0%, transparent 100%)',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ fontFamily: '"DM Sans", sans-serif' }}
                    >
                        Support Hub
                    </Typography>
                    <Tooltip title="Close">
                        <IconButton size="small" onClick={closePanel} aria-label="Close Support Hub">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* ── Search ── */}
                <Box sx={{ px: 1.5, py: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search help..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchQuery('')}
                                        aria-label="Clear search"
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                fontSize: '0.85rem',
                            },
                        }}
                    />
                </Box>

                {/* ── Tab Bar ── */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.25,
                        px: 1,
                        pb: 0.75,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <Tooltip key={tab.id} title={tab.label} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => handleTabClick(tab.id)}
                                    aria-label={tab.label}
                                    sx={{
                                        flex: 1,
                                        borderRadius: '10px',
                                        color: isActive
                                            ? theme.palette.secondary.main
                                            : 'text.secondary',
                                        bgcolor: isActive
                                            ? alpha(theme.palette.secondary.main, 0.12)
                                            : 'transparent',
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            bgcolor: isActive
                                                ? alpha(theme.palette.secondary.main, 0.18)
                                                : 'action.hover',
                                        },
                                    }}
                                    data-testid={`support-hub-tab-${tab.id}`}
                                >
                                    {tab.icon}
                                </IconButton>
                            </Tooltip>
                        );
                    })}
                </Box>

                {/* ── Tab Content ── */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 1.5,
                    }}
                >
                    {activeTabConfig.component}
                </Box>
            </Box>
        </Grow>
    );
};

export default SupportHubPanel;
